/**
 * Archer Production OS — canonical domain model.
 *
 * Time rule: every instant on the Mission timeline is a UTC epoch in milliseconds (`EpochMs`).
 * Never store local time. Display converts to UTC / mission-local at the edge.
 *
 * Units rule (storage + API): lat/lon WGS84 degrees, distance metres, aircraft altitude feet MSL,
 * speed knots, vertical speed ft/min, headings/bearings degrees TRUE [0,360).
 */

export type EpochMs = number;
export type Id = string;

export interface LatLon {
  lat: number;
  lon: number;
}

/** How a value came to exist. Always surfaced in UI where it matters. */
export type DataQuality =
  | 'observed' // a real sample at this instant (or within ~1.5 s)
  | 'interpolated' // between two real samples
  | 'estimated' // dead-reckoned beyond the last sample
  | 'predicted' // forecast/projection (ETA, CPA, future track)
  | 'manual'; // entered by a person

/** Age classification of the newest sample. */
export type Freshness = 'live' | 'aging' | 'stale' | 'lost' | 'none';

// ───────────────────────────── Users / auth ─────────────────────────────

/**
 * Where an account's aircraft data comes from. `public` = Airview Studio's free public feeds (adsb.lol…),
 * reserved for a few accounts; `own_key` (the default for everyone else) = only the account's own
 * FlightAware AeroAPI key, at the account's own cost.
 */
export type FeedAccess = 'public' | 'own_key';

export interface User {
  id: Id;
  email: string;
  displayName: string;
  isSimulated: boolean;
  feedAccess: FeedAccess;
  createdAt: EpochMs;
}

export type Role = 'owner' | 'producer' | 'crew' | 'viewer';

// ───────────────────────────── Mission ─────────────────────────────

export type MissionStatus = 'planned' | 'live' | 'ended';

export type MissionKind =
  | 'flight_test'
  | 'airport_ops'
  | 'remote'
  | 'acoustics'
  | 'demo'
  | 'multicam'
  | 'air_to_air'
  | 'other';

export type FrameRate =
  | '23.976'
  | '24'
  | '25'
  | '29.97'
  | '29.97DF'
  | '30'
  | '48'
  | '50'
  | '59.94'
  | '59.94DF'
  | '60';

export interface SimulationRef {
  scenarioId: string;
  runId: string;
  state: 'running' | 'stopped' | 'completed';
  mode: 'live' | 'historical';
}

export interface Mission {
  /** Archived missions retain all configuration and recorded history; null/absent means active. */
  archivedAt?: EpochMs | null;
  id: Id;
  /** Short human identifier shown on the timecode slate, e.g. "ACX-0929-A". */
  code: string;
  name: string;
  kind: MissionKind;
  status: MissionStatus;
  /** Planned operating window. */
  windowStart: EpochMs;
  windowEnd: EpochMs;
  actualStart: EpochMs | null;
  actualEnd: EpochMs | null;
  /** IANA zone for local display, e.g. "America/Los_Angeles". */
  timezone: string;
  center: LatLon;
  notes: string;
  frameRate: FrameRate;
  simulation: SimulationRef | null;
  createdBy: Id;
  createdAt: EpochMs;
  updatedAt: EpochMs;
  /**
   * Zone the cameras' time-of-day timecode runs in (aviation data spec §10): 'local' = `timezone`
   * (recommended, crew familiarity) or 'UTC'. Optional in the type; the server always sets it (default 'local').
   */
  tcZone?: TcZone;
}

/** Time-of-day timecode zone: mission local time or UTC. */
export type TcZone = 'local' | 'UTC';

export interface MissionMember {
  missionId: Id;
  userId: Id;
  role: Role;
  displayName: string;
  /** Short radio-style label, e.g. "JAKE". Used in alerts. */
  callsign: string;
  /** Hex colour for the crew marker. */
  color: string;
  sharingLocation: boolean;
  isSimulated: boolean;
  joinedAt: EpochMs;
  /**
   * Shoot lead (spec §7.4): may send broadcasts without being producer+. Settable by producer+.
   * Optional in the type for backward compatibility; the server always sets it.
   */
  isShootLead?: boolean;
}

// ───────────────────────────── Crew (people on the shoot) ─────────────────────────────

/**
 * What someone does on the shoot (their job on the call sheet). Separate from `Role`, which is
 * what they may change in the app (owner / producer / crew / viewer).
 */
export type CrewJob = 'producer' | 'director' | 'camera_op' | 'photographer' | 'drone_op' | 'chase_pilot' | 'audio' | 'pa' | 'other';

/**
 * A person on the shoot: an app user (`userId` set; every mission member has one) or someone
 * without the app (name, optional phone). Cameras point at their operator with `Camera.personId`.
 */
export interface Person {
  id: Id;
  missionId: Id;
  /** The mission member this person is; null = no app. */
  userId: Id | null;
  /** Display name (an app user's is their account name when the row was made). */
  name: string;
  phone: string | null;
  job: CrewJob | null;
  /** The place (MissionLocation) they are assigned to. */
  locationId: Id | null;
  /** "Call time 6:30", "bring the 600 mm". */
  notes: string;
  createdAt: EpochMs;
  updatedAt: EpochMs;
}

export interface MissionInvite {
  code: string;
  missionId: Id;
  role: Exclude<Role, 'owner'>;
  createdBy: Id;
  createdAt: EpochMs;
  expiresAt: EpochMs;
  maxUses: number | null;
  uses: number;
}

// ───────────────────────────── Aircraft ─────────────────────────────

export type AircraftRole = 'subject' | 'photo_ship' | 'chase' | 'other';

/**
 * Which provider feeds this aircraft's telemetry.
 * 'public' = composite of public sources (FlightAware when keyed, adsb.lol, opt-in aggregators, local
 * receiver), freshest fix wins — the default for real aircraft. 'crew_device' = mirrored from a crew
 * member's phone (`Aircraft.sourceUserId`), e.g. the photo ship.
 */
export type AircraftProviderId = 'simulation' | 'flightaware' | 'adsb' | 'manual' | 'companion' | 'public' | 'crew_device';

export interface Aircraft {
  id: Id;
  missionId: Id;
  /** Display name, e.g. "HERON H01". Used in alert titles. */
  label: string;
  registration: string | null; // e.g. N555VX
  /** ICAO 24-bit address, lowercase hex, for ADS-B providers. */
  icao24: string | null;
  /** FlightAware AeroAPI ident (registration or flight ident). */
  flightAwareIdent: string | null;
  typeDesignator: string | null; // e.g. "Heron HX-1", "AS350"
  role: AircraftRole;
  /** Polling priority is independent of production role; absent on older clients = normal. */
  trackingPriority?: 'normal' | 'high';
  color: string;
  provider: AircraftProviderId;
  /** Wingspan in metres for frame-fill estimates. */
  wingspanM: number | null;
  createdAt: EpochMs;
  /** provider 'crew_device': the member whose phone reports this aircraft's position. */
  sourceUserId?: Id | null;
}

export type AltitudeSource = 'baro' | 'geo' | null;

export interface AircraftObservation {
  missionId: Id;
  aircraftId: Id;
  /** Time the position was valid (sensor/report time). */
  t: EpochMs;
  /** Time the server received it. `receivedAt - t` = latency. */
  receivedAt: EpochMs;
  lat: number;
  lon: number;
  altFt: number | null; // MSL
  altSource: AltitudeSource;
  gsKt: number | null;
  trackDeg: number | null;
  vsFpm: number | null;
  /** Provider string, e.g. 'flightaware', 'adsb.lol', 'simulation'. */
  source: string;
  quality: DataQuality;
  /** Flight phase reported by telemetry (absent for ADS-B/FlightAware — then it is inferred). */
  phase?: FlightPhase | null;
  /**
   * Vertical reference of `altFt` (aviation data spec §3.3). Absent = MSL (legacy rows).
   * 'PA' = pressure altitude (no altimeter setting was available — UI says "PRESS ALT");
   * 'HAE' = GNSS height above the WGS84 ellipsoid (no geoid model for this location).
   */
  altRef?: AltitudeRef | null;
  /** How altFt was produced: 'reported', 'baro+altimeter' (PA corrected with a METAR altimeter), 'geo-geoid' (HAE − geoid). */
  altMethod?: string | null;
  /** ADS-B Navigation Accuracy Category for position (0–11). */
  nacp?: number | null;
  /** 95% horizontal position accuracy (m), from NACp (DO-260B) or the source; MLAT is widened. */
  accuracyM?: number | null;
}

export type AltitudeRef = 'MSL' | 'PA' | 'HAE';

/** eVTOL flight phase (spec §5.2). Inferred phases are flagged `estimated`. */
export type FlightPhase = 'LANDED' | 'TAXI' | 'HOVER' | 'TRANSITION' | 'CRUISE' | 'APPROACH' | 'UNKNOWN';

// ───────────────────────────── Crew ─────────────────────────────

export type CrewSource = 'browser' | 'ios' | 'simulation' | 'manual';

export interface CrewObservation {
  missionId: Id;
  userId: Id;
  t: EpochMs;
  receivedAt: EpochMs;
  lat: number;
  lon: number;
  /** Horizontal GPS accuracy radius (m), from the device. */
  accuracyM: number | null;
  altM: number | null; // metres MSL/ellipsoid as reported
  speedMps: number | null;
  headingDeg: number | null;
  source: CrewSource;
  /**
   * Which way the phone pointed (°T, compass), sent only while it is likely aimed (held up / the HUD
   * open). Distinct from headingDeg (the GPS course while walking). Optional: absent on old data.
   */
  facingDeg?: number | null;
  /** ± degrees the platform reported for facingDeg (null = not reported). */
  facingAccuracyDeg?: number | null;
  /** When the compass read facingDeg (server time; may be a little before t). */
  facingT?: EpochMs | null;
}

// ───────────────────────────── Cameras ─────────────────────────────

/** crew = travels with operator; static = fixed unmanned; manual = placed by hand; aircraft = mounted on/in an aircraft (e.g. photo ship). */
export type CameraMount = 'crew' | 'static' | 'manual' | 'aircraft';

export interface Camera {
  id: Id;
  missionId: Id;
  /** e.g. "CAM A". */
  label: string;
  body: string | null; // e.g. "ARRI ALEXA 35"
  lens: string | null; // e.g. "Fujinon 25-300"
  /** A prime's focal length. For a zoom (focalMinMm–focalMaxMm) clients store the long end here too, for old readers. */
  focalLengthMm: number | null;
  /** Zoom lens range (mm), e.g. 70–200. Both set (min < max) = a zoom; absent/null = a prime at focalLengthMm. */
  focalMinMm?: number | null;
  focalMaxMm?: number | null;
  sensorWidthMm: number | null;
  sensorHeightMm: number | null;
  mount: CameraMount;
  operatorUserId: Id | null;
  /**
   * Someone without the app who runs this camera ("Kofi (photographer)"). With no operatorUserId the
   * camera stays at `position`. Optional in the type for backward compatibility; the server always sets it.
   */
  operatorName?: string | null;
  /** The place (MissionLocation) this camera stands at; its position follows the place. Optional for compatibility. */
  locationId?: Id | null;
  /**
   * The person (Person) who runs this camera — an app user or someone without the app. The server keeps
   * operatorUserId / operatorName in step with it. Optional for compatibility (older data has none).
   */
  personId?: Id | null;
  aircraftId: Id | null;
  /** Fixed position for static/manual cameras (and a manned camera whose operator has no app). */
  position: LatLon | null;
  /** Optional pointing direction, degrees true. */
  headingDeg: number | null;
  tiltDeg: number | null;
  recording: boolean;
  notes: string;
  createdAt: EpochMs;
  /** Timecode source on the camera (spec §9). Default 'TOD'. Optional in the type; the server always sets it. */
  tcSource?: TcSource;
  /** Camera TC minus slate time-of-day TC, in frames at the mission frame rate. Default 0. */
  tcOffsetFrames?: number;
  /**
   * Jam point (aviation data spec §10): the UTC instant the camera's TC generator was jammed and the TC
   * it read then. When both are set, camera TC ↔ UTC uses the exact frame rate from this point (23.976 /
   * 29.97 NDF drift 3.6 s per hour against the wall clock); otherwise wall-clock alignment + tcOffsetFrames.
   */
  jamUtc?: EpochMs | null;
  jamTc?: string | null;
}

/** EXT = external sync box, JAM = jammed from a reference, TOD = time-of-day clock. */
export type TcSource = 'TOD' | 'JAM' | 'EXT';

export type CameraObsSource = 'manual' | 'ios' | 'simulation' | 'camera' | 'browser';

export interface CameraObservation {
  missionId: Id;
  cameraId: Id;
  t: EpochMs;
  receivedAt: EpochMs;
  lat: number | null;
  lon: number | null;
  headingDeg: number | null;
  pitchDeg: number | null;
  rollDeg: number | null;
  focalLengthMm: number | null;
  recording: boolean | null;
  source: CameraObsSource;
}

// ───────────────────────────── Places ─────────────────────────────

export type LocationKind =
  | 'camera_site'
  | 'staging'
  | 'basecamp'
  | 'landing_zone'
  | 'poi'
  | 'hazard'
  | 'parking';

export interface MissionLocation {
  id: Id;
  missionId: Id;
  name: string;
  kind: LocationKind;
  lat: number;
  lon: number;
  elevM: number | null;
  radiusM: number | null;
  notes: string;
  createdAt: EpochMs;
}

/** Producer-authored aircraft route used for production and crew staging plans. */
export interface PlannedFlightRoute {
  missionId: Id;
  aircraftId: Id;
  name: string;
  waypoints: Array<LatLon & { name: string }>;
  updatedAt: EpochMs;
}


export interface Runway {
  ident: string; // "13/31"
  lengthFt: number;
  widthFt: number | null;
  /** Low-end threshold and high-end threshold. */
  le: LatLon & { ident: string; headingDeg: number };
  he: LatLon & { ident: string; headingDeg: number };
}

export interface Airport {
  icao: string;
  iata: string | null;
  name: string;
  city: string | null;
  lat: number;
  lon: number;
  elevFt: number;
  runways: Runway[];
  /** true if a METAR is normally published for this station. */
  hasMetar: boolean;
}

export type MissionAirportRole = 'primary' | 'alternate' | 'nearby';

export interface MissionAirport {
  missionId: Id;
  icao: string;
  role: MissionAirportRole;
}

// ───────────────────────────── Weather ─────────────────────────────

export type FlightCategory = 'VFR' | 'MVFR' | 'IFR' | 'LIFR' | 'UNKNOWN';

export type CloudCover = 'SKC' | 'CLR' | 'FEW' | 'SCT' | 'BKN' | 'OVC' | 'VV';

export interface CloudLayer {
  cover: CloudCover;
  baseFt: number | null; // AGL
}

export interface MetarReport {
  stationId: string;
  /** Observation time. */
  t: EpochMs;
  raw: string;
  windDirDeg: number | 'VRB' | null;
  windKt: number | null;
  gustKt: number | null;
  visibilitySm: number | null;
  /** As published, e.g. "10+". */
  visibilityText: string | null;
  ceilingFt: number | null; // lowest BKN/OVC/VV, AGL
  clouds: CloudLayer[];
  tempC: number | null;
  dewpointC: number | null;
  altimeterInHg: number | null;
  wx: string | null; // present weather, e.g. "-RA BR"
  flightCategory: FlightCategory;
}

export interface TafPeriod {
  from: EpochMs;
  to: EpochMs;
  changeType: 'FM' | 'TEMPO' | 'BECMG' | 'PROB' | null;
  probability: number | null;
  windDirDeg: number | 'VRB' | null;
  windKt: number | null;
  gustKt: number | null;
  visibilitySm: number | null;
  ceilingFt: number | null;
  clouds: CloudLayer[];
  wx: string | null;
  flightCategory: FlightCategory;
}

export interface TafReport {
  stationId: string;
  issued: EpochMs;
  validFrom: EpochMs;
  validTo: EpochMs;
  raw: string;
  periods: TafPeriod[];
}

export interface GeneralWeatherHour {
  t: EpochMs;
  tempC: number | null;
  cloudCoverPct: number | null;
  precipProbPct: number | null;
  precipMm: number | null;
  windKt: number | null;
  gustKt: number | null;
  windDirDeg: number | null;
  weatherCode: number | null; // WMO code (practice missions only; the NWS has none)
}

export interface GeneralWeather {
  t: EpochMs;
  lat: number;
  lon: number;
  tempC: number | null;
  apparentTempC: number | null;
  humidityPct: number | null;
  cloudCoverPct: number | null;
  precipMm: number | null;
  precipProbPct: number | null;
  windKt: number | null;
  gustKt: number | null;
  windDirDeg: number | null;
  visibilityM: number | null;
  weatherCode: number | null; // WMO code
  weatherText: string | null;
  hourly: GeneralWeatherHour[];
}

export type WeatherKind = 'metar' | 'taf' | 'general';

export type WeatherPayload =
  | { kind: 'metar'; report: MetarReport }
  | { kind: 'taf'; report: TafReport }
  | { kind: 'general'; report: GeneralWeather };

export interface WeatherObservation {
  id: Id;
  missionId: Id;
  /** Observation/issue time of the underlying report. */
  t: EpochMs;
  receivedAt: EpochMs;
  kind: WeatherKind;
  stationId: string | null;
  /** e.g. 'aviationweather.gov', 'nws', 'simulation' ('open-meteo' on forecasts recorded by older builds). */
  source: string;
  payload: WeatherPayload;
}

// ───────────────────────────── Markers / events / media / comms ─────────────────────────────

export type MarkerCategory = 'pass' | 'hero' | 'good' | 'missed' | 'issue' | 'note';

export interface Marker {
  id: Id;
  missionId: Id;
  t: EpochMs;
  createdAt: EpochMs;
  authorUserId: Id;
  authorName: string;
  label: string; // "HERO PASS"
  category: MarkerCategory;
  lat: number | null;
  lon: number | null;
  aircraftId: Id | null;
  cameraId: Id | null;
  notes: string;
  // ── Rich marks (spec §8). Optional in the type for backward compatibility; the server always sets them. ──
  /** M-n sequence number per mission, assigned by the server. */
  seq?: number;
  isHero?: boolean;
  /** Client idempotency key (offline outbox). */
  clientId?: string | null;
  /** Slate text at press time at the mission frame rate, e.g. "14:32:07:12". */
  tc?: string | null;
  tcSource?: TcSource;
  operatorLat?: number | null;
  operatorLon?: number | null;
  operatorAccuracyM?: number | null;
  subjectSnapshot?: MarkSubjectSnapshot | null;
  /** Suggested tags from state at `t` (Archer Content Vault taxonomy words). */
  autoTags?: string[];
  /** Operator-edited tags. */
  tags?: string[];
  /** Soft delete (undo). Deleted marks are hidden from lists/exports unless asked for. */
  deletedAt?: EpochMs | null;
  voiceNote?: MarkVoiceNote | null;
}

/** Subject aircraft state captured with a mark (computed server-side if the client does not send one). */
export interface MarkSubjectSnapshot {
  aircraftId: Id;
  lat: number;
  lon: number;
  altFt: number | null;
  phase: FlightPhase | null;
  /** Operator → subject horizontal distance (m); null when the operator position is unknown. */
  distanceM: number | null;
  /** Operator → subject bearing, degrees true. */
  bearingDeg: number | null;
  /** Clock position relative to the camera heading (or operator facing) when known. */
  clock: number | null;
  freshness: Freshness;
}

export interface MarkVoiceNote {
  mime: string;
  durationSec: number;
  bytes: number;
  /** Future: local faster-whisper transcription (as in the Archer Content Vault). Always null for now. */
  transcript: string | null;
}

export type MissionEventType =
  | 'mission_created'
  | 'mission_started'
  | 'mission_ended'
  | 'member_joined'
  | 'sharing_started'
  | 'sharing_stopped'
  | 'camera_recording_started'
  | 'camera_recording_stopped'
  | 'aircraft_data_lost'
  | 'aircraft_data_restored'
  | 'simulation_started'
  | 'simulation_stopped'
  | 'provider_error'
  | 'runsheet_advanced'
  | 'broadcast_sent'
  | 'note';

export interface MissionEvent {
  id: Id;
  missionId: Id;
  t: EpochMs;
  type: MissionEventType;
  actorUserId: Id | null;
  summary: string;
  data: Record<string, unknown>;
}

export type MediaClipSource = 'manual' | 'import' | 'media_nexus' | 'simulation';

export interface MediaClip {
  id: Id;
  missionId: Id;
  cameraId: Id | null;
  operatorUserId: Id | null;
  filename: string; // "A003_C017_0929XY.mov"
  /** Clip name as used by editorial, e.g. "A003_C017". */
  clipName: string | null;
  reel: string | null;
  startT: EpochMs;
  endT: EpochMs;
  durationSec: number;
  /** Source timecode at start, as recorded by the camera. */
  startTimecode: string | null;
  frameRate: FrameRate | null;
  source: MediaClipSource;
  /** Opaque id in an external system (e.g. Media Nexus asset id). */
  externalRef: string | null;
  notes: string;
  createdAt: EpochMs;
}

/** Crew comms: team chat on the shoot. */
export interface CommsMessage {
  id: Id;
  missionId: Id;
  t: EpochMs;
  authorUserId: Id;
  authorName: string;
  text: string;
  channel: 'content';
}

// ───────────────────────────── Alerts ─────────────────────────────

export type AlertSeverity = 'info' | 'advisory' | 'warning';

export type TargetRef =
  | { kind: 'crew'; id: Id } // userId
  | { kind: 'camera'; id: Id }
  | { kind: 'location'; id: Id }
  | { kind: 'aircraft'; id: Id }
  | { kind: 'airport'; id: string }; // icao

/** 'any' = every aircraft on the mission. */
export type AircraftSelector = Id | 'any';

export type SunEventName =
  | 'sunrise'
  | 'sunset'
  | 'golden_hour_morning_end'
  | 'golden_hour_evening_start'
  | 'civil_dawn'
  | 'civil_dusk';

export type AlertRuleSpec =
  | { type: 'proximity'; aircraft: AircraftSelector; target: TargetRef; thresholdNm: number }
  | { type: 'eta'; aircraft: AircraftSelector; target: TargetRef; thresholdSec: number }
  | { type: 'sun'; event: SunEventName; leadMin: number }
  | {
      type: 'weather_change';
      /** Station to watch; null = all mission airports + general weather. */
      stationId: string | null;
      windIncreaseKt: number;
      gustKt: number;
      onFlightCategoryChange: boolean;
      precipStart: boolean;
    }
  | { type: 'stale_data'; aircraft: AircraftSelector; thresholdSec: number }
  | { type: 'separation'; aircraftA: Id; aircraftB: Id; thresholdNm: number; direction: 'inside' | 'outside' }
  /** Closest point of approach within `thresholdSec`, and no farther than `maxCpaNm` from the target. */
  | { type: 'cpa'; aircraft: AircraftSelector; target: TargetRef; thresholdSec: number; maxCpaNm: number }
  /** Takeoff / landing detected from the (inferred or reported) flight phase. */
  | { type: 'phase_change'; aircraft: AircraftSelector; events: Array<'takeoff' | 'landing'> }
  /** Countdown callouts before each scheduled run-sheet item (seconds before `scheduledT`). */
  | { type: 'runsheet_due'; leadSec: number[] }
  /** A crew member is more than `thresholdM` from the planned position of a camera they operate (mission window only). */
  | { type: 'crew_off_position'; thresholdM: number }
  /**
   * Non-mission traffic within `radiusNm` and ±`altBandFt` of an active subject, or inside the working area
   * (within `workingAreaNm` of a camera / camera site). Evaluated server-side with the traffic picture. Tier 3.
   */
  | { type: 'traffic_near_subject'; aircraft: AircraftSelector; radiusNm: number; altBandFt: number; workingAreaNm: number }
  /** Subject predicted to become terrain-masked from a camera / camera site within `horizonSec` (DEM, dead reckoning). Tier 3. */
  | { type: 'terrain_mask'; aircraft: AircraftSelector; horizonSec: number };

export interface AlertRule {
  id: Id;
  missionId: Id;
  name: string;
  enabled: boolean;
  severity: AlertSeverity;
  /** Minimum seconds between two firings of the same (rule, subject). */
  cooldownSec: number;
  spec: AlertRuleSpec;
  createdAt: EpochMs;
}

/**
 * Spec §7.1 numbering: 2 = action (spoken callout), 3 = info. Product decision: this is a content
 * tool with NO critical tier — `1` stays in the type for spec compatibility but nothing produces it,
 * nothing overrides mute, and acknowledgement is never required.
 */
export type AlertTier = 1 | 2 | 3;

export interface Alert {
  id: Id;
  missionId: Id;
  ruleId: Id | null;
  t: EpochMs;
  severity: AlertSeverity;
  /** Uppercase headline, e.g. "HERON H01 — 5 NM FROM JAKE". */
  title: string;
  body: string;
  /** Stable key: same situation → same key (used for de-duplication). */
  dedupeKey: string;
  subjects: TargetRef[];
  ackedBy: Id[];
  /** Optional in the type for backward compatibility; the evaluator and server always set it. */
  tier?: AlertTier;
  /** Text for TTS in the fixed grammar "[who], [clock], [distance], [trend/event]." (null = nothing to say). */
  callout?: string | null;
}

/** An alert produced by the pure evaluator, before persistence. */
export type AlertDraft = Omit<Alert, 'id' | 'missionId' | 'ackedBy'>;

// ───────────────────────────── Reconstructed world ─────────────────────────────

export interface TrailPoint {
  t: EpochMs;
  lat: number;
  lon: number;
  altFt: number | null;
}

export interface AircraftState {
  aircraftId: Id;
  /** Time this state describes (the query time). */
  t: EpochMs;
  lat: number;
  lon: number;
  altFt: number | null;
  gsKt: number | null;
  trackDeg: number | null;
  vsFpm: number | null;
  quality: DataQuality;
  /** Time of the newest real sample at or before `t`. */
  lastObservedT: EpochMs;
  /** Seconds between `t` and `lastObservedT`. */
  ageSec: number;
  freshness: Freshness;
  source: string;
  /** Recent track, oldest first, ending at or before `t`. */
  trail: TrailPoint[];
  /** 95% horizontal accuracy (m) of the newest sample, when the source reports it. */
  accuracyM?: number | null;
  /** NACp < 8 (≥ 93 m), or an MLAT-derived position. */
  /** Dead-reckoned position: display uncertainty radius (m), growing with time since the last fix. */
  estimateM?: number;
  lowIntegrity?: boolean;
  /** Vertical reference of altFt (absent = MSL). */
  altRef?: AltitudeRef | null;
  /** Flight phase (set by buildWorldState). */
  phase?: FlightPhase;
  /** true when the phase was inferred rather than reported by telemetry. */
  phaseEstimated?: boolean;
  /** DEM ground elevation (m MSL) under the aircraft, when the terrain tile was available (class R). */
  groundElevM?: number | null;
  /** altFt (MSL) − ground elevation (ft). Class C; withheld for unconverted PA/HAE. */
  aglFt?: number | null;
}

export interface CrewState {
  userId: Id;
  t: EpochMs;
  lat: number;
  lon: number;
  accuracyM: number | null;
  speedMps: number | null;
  headingDeg: number | null;
  quality: DataQuality;
  lastObservedT: EpochMs;
  ageSec: number;
  freshness: Freshness;
  trail: TrailPoint[];
  /** Newest phone facing at or before t (°T, see CrewObservation.facingDeg); absent when none was sent recently. */
  facingDeg?: number | null;
  facingAccuracyDeg?: number | null;
  facingT?: EpochMs | null;
}

/**
 * How a camera's pointing is known: 'set' = configured / reported heading (static), 'live' = a
 * manned camera aimed by its operator's phone facing (or a fresh camera-reported heading),
 * 'unknown' = a manned camera whose aim is stale or inaccurate (coverage falls back to "can cover").
 */
export type CameraAim = 'set' | 'live' | 'unknown';

export interface CameraState {
  cameraId: Id;
  t: EpochMs;
  /** null if the camera has no resolvable position at `t`. */
  position: LatLon | null;
  positionSource: 'operator' | 'static' | 'manual' | 'aircraft' | 'observation' | 'none';
  headingDeg: number | null;
  focalLengthMm: number | null;
  hfovDeg: number | null;
  recording: boolean;
  freshness: Freshness;
  /** How headingDeg is known (optional for compatibility). */
  aim?: CameraAim;
  /** Age (s) of the live aim for a manned camera; null otherwise. */
  aimAgeSec?: number | null;
  /** Zoom: horizontal FOV at the wide (focalMinMm) and long (focalMaxMm) ends; equal for a prime. */
  hfovWideDeg?: number | null;
  hfovTeleDeg?: number | null;
}

export interface SunState {
  t: EpochMs;
  azimuthDeg: number;
  elevationDeg: number;
  phase: LightPhase;
}

export type LightPhase = 'night' | 'astronomical_twilight' | 'nautical_twilight' | 'blue_hour' | 'golden_hour' | 'day';

export interface WorldState {
  missionId: Id;
  t: EpochMs;
  aircraft: AircraftState[];
  crew: CrewState[];
  cameras: CameraState[];
  sun: SunState;
  /** Newest METAR per station at or before t. */
  metars: MetarReport[];
  /** Newest TAF per station issued at or before t. */
  tafs: TafReport[];
  general: GeneralWeather | null;
  /** Media clips whose [startT, endT] contains t. */
  activeMedia: MediaClip[];
  /** Markers within ±`markerWindowSec` of t (default 30 s). */
  nearbyMarkers: Marker[];
  /** Alerts fired within the last `alertWindowSec` (default 120 s) before t. */
  recentAlerts: Alert[];
}

/** Everything needed to reconstruct the world for a time range. Server returns this for Replay. */
export interface MissionRecord {
  mission: Mission;
  members: MissionMember[];
  aircraft: Aircraft[];
  cameras: Camera[];
  locations: MissionLocation[];
  airports: Airport[];
  aircraftObs: Record<Id, AircraftObservation[]>; // by aircraftId, sorted by t
  crewObs: Record<Id, CrewObservation[]>; // by userId, sorted by t
  cameraObs: Record<Id, CameraObservation[]>; // by cameraId, sorted by t
  weather: WeatherObservation[]; // sorted by t
  markers: Marker[];
  alerts: Alert[];
  events: MissionEvent[];
  mediaClips: MediaClip[];
  comms: CommsMessage[];
  /** Run sheet / test card, by `order`. Optional in the type; the server always sets it. */
  runSheet?: RunSheetItem[];
  /** Shoot-lead broadcasts, by t. Optional in the type; the server always sets it. */
  broadcasts?: Broadcast[];
  /** People on the shoot and their assignments (current, not historical). Optional; the server always sets it. */
  people?: Person[];
}

// ───────────────────────────── Run sheet / broadcasts / audit ─────────────────────────────

export type RunSheetStatus = 'pending' | 'active' | 'done' | 'skipped';

/** One test-card / run-sheet line (spec §3.2, §5.2 Next event, §11.1 step 4). */
export interface RunSheetItem {
  id: Id;
  missionId: Id;
  /** 1-based position in the sheet. */
  order: number;
  label: string;
  scheduledT: EpochMs | null;
  durationSec: number | null;
  notes: string;
  status: RunSheetStatus;
  startedT: EpochMs | null;
  completedT: EpochMs | null;
  createdAt: EpochMs;
}

/**
 * Broadcast calls (crewComms.ts has labels, spoken lines and the likely-next call). Flight-phase
 * calls relay what the aircraft is doing (the human call is the reliable signal when ADS-B lags);
 * production calls coordinate cameras. The original five presets keep their values, so stored rows
 * still read. No safety/clearance presets (CLEAR_AREA was dropped; "pass complete", never "clear").
 */
export type BroadcastPreset =
  | 'ENGINES_START' | 'TAXIING' | 'TAKEOFF' | 'ON_STATION' | 'STARTING_PASS' | 'PASS_COMPLETE' | 'RTB' | 'LANDING' | 'SHUTDOWN'
  | 'HOLD' | 'GO' | 'RESET' | 'NEXT_PASS' | 'REPOSITION' | 'WRAP'
  | 'CUSTOM';

export interface BroadcastReceipt {
  userId: Id;
  receivedAt: EpochMs | null;
  ackedAt: EpochMs | null;
}

/** Shoot-lead broadcast (spec §7.4). Human content-coordination message, always tier 2; shown with the sender's name. Ack = optional "seen". */
export interface Broadcast {
  id: Id;
  missionId: Id;
  t: EpochMs;
  fromUserId: Id;
  fromName: string;
  preset: BroadcastPreset;
  text: string;
  tier: AlertTier;
  /** One per recipient (every member except the sender at send time, plus late joiners who report). */
  receipts: BroadcastReceipt[];
  /** The Alert created for this broadcast (flows through the normal alert UI/history). */
  alertId: Id | null;
}

export interface AuditLogEntry {
  id: Id;
  userId: Id;
  missionId: Id;
  /** e.g. 'export.marks_csv', 'export.premiere_xml'. */
  action: string;
  detail: Record<string, unknown>;
  t: EpochMs;
}

/** One public source's view of one aircraft. */
export interface AircraftSourceState {
  /** e.g. 'flightaware', 'adsb.lol', 'adsb.fi', 'airplanes.live', 'adsb.local', 'crew_device', 'simulation'. */
  id: string;
  name: string;
  enabled: boolean;
  /** `fallback` sources are queried only while the primaries cannot give a fresh fix. */
  role?: 'primary' | 'fallback';
  /** Last poll attempt / success (ms). */
  lastPollAt: EpochMs | null;
  lastOkAt: EpochMs | null;
  /** `t` of the newest position this source reported. */
  lastSeenT: EpochMs | null;
  lastError: string | null;
  /** Licensing / attribution note for the UI (e.g. ODbL attribution, "personal / non-commercial use only"). */
  notes: string;
}

/**
 * Per-aircraft provider status (GET /api/missions/:id/aircraft/:aircraftId/sources, WS
 * `aircraft_sources`). `state` drives the honest empty state: flight-test aircraft may never
 * appear on public ADS-B (no transponder data, LADD/PIA filtering, MLAT gaps).
 */
export interface AircraftSourcesStatus {
  aircraftId: Id;
  provider: AircraftProviderId;
  /** Resolved ICAO24 (from the aircraft, the N-number algorithm or a /reg lookup). */
  icao24: string | null;
  /** `no_key`: the owner's account has no usable FlightAware key (feed access `own_key`), so nothing is tracked. */
  state: 'live' | 'no_public_position' | 'never_seen' | 'not_tracking' | 'no_key';
  /** Newest position from any source. */
  lastSeenT: EpochMs | null;
  lastSource: string | null;
  /** e.g. "not seen on ADS-B/FlightAware since 14:31Z", "never seen this mission". */
  reason: string | null;
  sources: AircraftSourceState[];
  updatedAt: EpochMs;
}

/** Shoot-lead console row (spec §6, §16): GET /api/missions/:id/crew-board. */
export interface CrewBoardEntry {
  userId: Id;
  callsign: string;
  displayName: string;
  role: Role;
  color: string;
  isShootLead: boolean;
  sharingLocation: boolean;
  /** Current state (null when no fix). */
  position: { lat: number; lon: number; accuracyM: number | null; t: EpochMs; ageSec: number } | null;
  freshness: Freshness;
  cameras: Array<{
    cameraId: Id;
    label: string;
    /** Planned position (Camera.position); null when not planned. */
    plannedPosition: LatLon | null;
    /** Current distance from the planned position (m). */
    offsetM: number | null;
    /** > 50 m from the planned position. */
    offPosition: boolean;
  }>;
  marksCount: number;
  lastMarkT: EpochMs | null;
}

// ───────────────────────────── API bundle ─────────────────────────────

/** GET /api/missions/:id — the mission's configuration (not telemetry). */
export interface MissionBundle {
  mission: Mission;
  myRole: Role;
  me: MissionMember;
  members: MissionMember[];
  aircraft: Aircraft[];
  cameras: Camera[];
  locations: MissionLocation[];
  plannedRoutes?: PlannedFlightRoute[];
  airports: Airport[];
  missionAirports: MissionAirport[];
  alertRules: AlertRule[];
  /** Optional in the type for backward compatibility; the server always sets it. */
  runSheet?: RunSheetItem[];
  /** Everyone on the shoot (members and people without the app). Optional for compatibility; the server always sets it. */
  people?: Person[];
}

export interface MissionSummary {
  mission: Mission;
  myRole: Role;
  memberCount: number;
  aircraftCount: number;
}

// ───────────────────────────── Traffic context ─────────────────────────────

/** One non-mission aircraft seen on public / local ADS-B around the mission (advisory context only). */
export interface TrafficTarget {
  /** Opaque FlightAware flight identifier, present only for live AeroAPI traffic. */
  faFlightId?: string;
  /** ICAO 24-bit address, lowercase hex ("~…" for non-ICAO/TIS-B addresses). */
  hex: string;
  callsign: string | null;
  registration: string | null;
  /** ICAO type designator, e.g. "C172". */
  typeCode: string | null;
  lat: number;
  lon: number;
  /** Feet in `altRef`; null when not reported. */
  altFt: number | null;
  /** 'PA' = pressure altitude (ADS-B baro, uncorrected); 'MSL' when corrected with an altimeter; 'HAE' = GNSS geometric. */
  altRef: AltitudeRef | null;
  gsKt: number | null;
  trackDeg: number | null;
  vsFpm: number | null;
  /** When the position was valid (epoch ms). */
  t: EpochMs;
  /** 'adsb.lol', 'adsb.local', 'simulation'… */
  source: string;
  /** ADS-B emitter category ("A1" light, "A7" rotorcraft, …); null when unknown. */
  category: string | null;
  /** true when the aircraft reports on the ground. */
  onGround?: boolean;
}

// ───────────────────────────── Realtime (WebSocket) ─────────────────────────────

export type BundleEntity =
  | 'mission'
  | 'members'
  | 'aircraft'
  | 'cameras'
  | 'locations'
  | 'planned_routes'
  | 'airports'
  | 'alert_rules'
  | 'media'
  | 'runsheet'
  | 'people';

export type ServerMessage =
  | { type: 'hello'; serverTime: EpochMs; missionId: Id; userId: Id }
  | { type: 'pong'; clientTime: EpochMs; serverTime: EpochMs }
  | { type: 'aircraft_obs'; data: AircraftObservation }
  | { type: 'crew_obs'; data: CrewObservation }
  | { type: 'camera_obs'; data: CameraObservation }
  | { type: 'weather'; data: WeatherObservation }
  | { type: 'marker'; op: 'upsert' | 'delete'; data: Marker }
  | { type: 'alert'; data: Alert }
  | { type: 'event'; data: MissionEvent }
  | { type: 'media'; op: 'upsert' | 'delete'; data: MediaClip }
  | { type: 'comms'; data: CommsMessage }
  | { type: 'mission'; data: Mission }
  | { type: 'bundle_changed'; entity: BundleEntity }
  | { type: 'runsheet'; op: 'upsert' | 'delete'; data: RunSheetItem }
  | { type: 'broadcast'; data: Broadcast }
  /** Schedule & shots changed (a save, or a shot ticked off in Live); clients refetch at this revision. */
  | { type: 'plan'; revision: number }
  | { type: 'aircraft_sources'; data: AircraftSourcesStatus }
  /** Current non-mission traffic picture around the mission (advisory context; replaces the previous picture). */
  | { type: 'traffic'; data: TrafficTarget[] }
  /** A spoken update for ONE user (sent only to that user's sockets on the mission). */
  | { type: 'callout'; data: Callout }
  /**
   * Round 4: plain status of the focus aircraft from ONE member's side (statusFeed.ts) — sent only to
   * that member's sockets that asked for it (`/api/ws?…&feeds=status`, the iOS app's Live Activity).
   */
  | { type: 'status'; data: import('./statusFeed.ts').StatusFeed }
  | { type: 'error'; message: string };

// ───────────────────────────── Spoken updates (callouts) ─────────────────────────────

/** Spoken updates the announcer computes from aircraft positions. */
export type AircraftCalloutKind = 'takeoff' | 'landing' | 'range' | 'closest' | 'visible' | 'hidden' | 'data_lost' | 'data_back' | 'periodic';
/** + a producer's broadcast call, spoken to every listener. */
export type CalloutKind = AircraftCalloutKind | 'broadcast';

/**
 * A spoken update computed on the server for one listener (so the native app can speak it with the
 * web view suspended). Informational only.
 */
export interface Callout {
  /** Unique; clients de-duplicate on it. */
  id: string;
  missionId: Id;
  /** Recipient. */
  userId: Id;
  /** Server time of the event. */
  t: EpochMs;
  kind: CalloutKind;
  aircraftId: Id | null;
  /** Exactly what to say, TTS-ready ("N five five five V X has taken off."). */
  text: string;
}

export type CalloutPeriodicSec = 0 | 30 | 60 | 120;

/** Per-user spoken-update preferences (server-stored; GET/PUT /api/me/callout-prefs). */
export interface CalloutPrefs {
  enabled: boolean;
  /** null = every Subject + Chase aircraft on the mission. */
  aircraftIds: Id[] | null;
  /** Distance + direction every N s (0 = off). */
  periodicSec: CalloutPeriodicSec;
}

export const DEFAULT_CALLOUT_PREFS: CalloutPrefs = Object.freeze({ enabled: false, aircraftIds: null, periodicSec: 0 }) as CalloutPrefs;

export type ClientMessage = { type: 'ping'; clientTime: EpochMs };

// ───────────────────────────── Simulation catalogue ─────────────────────────────

export interface ScenarioInfo {
  id: string;
  name: string;
  description: string;
  durationSec: number;
  kind: MissionKind;
}

export interface ProviderStatus {
  id: string;
  name: string;
  kind: 'aircraft' | 'aviation_weather' | 'general_weather' | 'airports';
  configured: boolean;
  live: boolean;
  notes: string;
}
